import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../lib/api';
import { LayoutDashboard, Package, ShoppingBag, Recycle, Star, ArrowLeft } from 'lucide-react';

export default function AdminPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !isAdmin) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#4A5D23] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isMainAdmin = location.pathname === '/admin';

  const menuItems = [
    { path: '/admin/products', label: 'Prodotti', icon: Package },
    { path: '/admin/orders', label: 'Ordini', icon: ShoppingBag },
    { path: '/admin/trade-ins', label: 'Trade-Ins', icon: Recycle },
    { path: '/admin/reviews', label: 'Recensioni', icon: Star },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl md:text-4xl font-light tracking-tight">
            Pannello Admin
          </h1>
          <Link
            to="/account"
            className="text-[#57534E] hover:text-[#1C1917] flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna all'Account
          </Link>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              <Link
                to="/admin"
                data-testid="admin-nav-dashboard"
                className={`flex items-center gap-3 p-4 transition-colors ${
                  isMainAdmin
                    ? 'bg-[#1C1917] text-white'
                    : 'bg-[#F2EFE9] hover:bg-[#E5E0D8]'
                }`}
              >
                <LayoutDashboard className="w-5 h-5" />
                <span>Dashboard</span>
              </Link>
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.path.split('/').pop()}`}
                  className={`flex items-center gap-3 p-4 transition-colors ${
                    location.pathname.startsWith(item.path)
                      ? 'bg-[#1C1917] text-white'
                      : 'bg-[#F2EFE9] hover:bg-[#E5E0D8]'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-4">
            {isMainAdmin ? (
              <div className="space-y-8">
                {/* Stats Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-[#F2EFE9] p-6" data-testid="stat-revenue">
                    <p className="text-sm text-[#57534E] mb-1">Fatturato Totale</p>
                    <p className="text-3xl font-light">
                      {loading ? '...' : `€${stats?.total_revenue?.toFixed(2) || 0}`}
                    </p>
                  </div>
                  <div className="bg-[#F2EFE9] p-6" data-testid="stat-orders">
                    <p className="text-sm text-[#57534E] mb-1">Ordini Totali</p>
                    <p className="text-3xl font-light">
                      {loading ? '...' : stats?.total_orders || 0}
                    </p>
                    <p className="text-sm text-[#C05746] mt-1">
                      {stats?.pending_orders || 0} in attesa
                    </p>
                  </div>
                  <div className="bg-[#F2EFE9] p-6" data-testid="stat-products">
                    <p className="text-sm text-[#57534E] mb-1">Prodotti</p>
                    <p className="text-3xl font-light">
                      {loading ? '...' : stats?.total_products || 0}
                    </p>
                  </div>
                  <div className="bg-[#F2EFE9] p-6" data-testid="stat-trade-ins">
                    <p className="text-sm text-[#57534E] mb-1">Biolosophy Value</p>
                    <p className="text-3xl font-light">
                      {loading ? '...' : stats?.total_trade_ins || 0}
                    </p>
                    <p className="text-sm text-[#4A5D23] mt-1">
                      {stats?.pending_trade_ins || 0} da gestire
                    </p>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="grid md:grid-cols-3 gap-6">
                  <Link
                    to="/admin/orders"
                    className="bg-[#4A5D23] text-white p-6 hover:bg-[#3B4A1C] transition-colors"
                  >
                    <ShoppingBag className="w-8 h-8 mb-4" />
                    <h3 className="font-medium mb-1">Gestisci Ordini</h3>
                    <p className="text-white/70 text-sm">Visualizza e aggiorna gli ordini</p>
                  </Link>
                  <Link
                    to="/admin/products"
                    className="bg-[#1C1917] text-white p-6 hover:bg-[#57534E] transition-colors"
                  >
                    <Package className="w-8 h-8 mb-4" />
                    <h3 className="font-medium mb-1">Gestisci Prodotti</h3>
                    <p className="text-white/70 text-sm">Aggiungi e modifica prodotti</p>
                  </Link>
                  <Link
                    to="/admin/trade-ins"
                    className="bg-[#C05746] text-white p-6 hover:bg-[#a84637] transition-colors"
                  >
                    <Recycle className="w-8 h-8 mb-4" />
                    <h3 className="font-medium mb-1">Biolosophy Value</h3>
                    <p className="text-white/70 text-sm">Gestisci le richieste Biolosophy Value</p>
                  </Link>
                </div>
              </div>
            ) : (
              <Outlet context={{ refreshStats: fetchStats }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
